// Shared helper functions across the application
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";

dayjs.extend(duration);

export function formatDifferenceDuration(from, to, gracePeriod = 3000) {
  const diff = dayjs(to).diff(dayjs(from));
  if (diff < gracePeriod) return "—";
  const dur = dayjs.duration(diff);
  const days = Math.floor(dur.asDays());
  const hours = dur.hours();
  const minutes = dur.minutes();
  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  return parts.length === 0 ? "Superfast ⚡" : parts.join(" ");
}

/**
 * Returns true if the authenticated user is allowed to access a route entry.
 * Rules:
 *   - rootOnly: true   → only allowed when isRootUser is true
 *   - ownerOnly: true  → only allowed when isOwner is true (account owner,
 *                        not a delegated member)
 *   - requiresCodesEnabled: true → only allowed when the discount codes
 *                        switch is on (codesEnabled; unknown counts as off)
 *   - no flag          → allowed for everyone
 *
 * @param {{ rootOnly?: boolean, ownerOnly?: boolean, requiresCodesEnabled?: boolean }} routeConfig - a sideMenuConfig entry
 * @param {{ isRootUser?: boolean, isOwner?: boolean, codesEnabled?: boolean }} auth
 * @returns {boolean}
 */
export function isRouteAllowed(
  routeConfig,
  { isRootUser, isOwner, codesEnabled }
) {
  if (routeConfig.rootOnly && !isRootUser) return false;
  if (routeConfig.ownerOnly && !isOwner) return false;
  if (routeConfig.requiresCodesEnabled && !codesEnabled) return false;
  return true;
}

/**
 * Extracts the URL from a media array.
 * Returns the value of the is_primary item, falling back to the first item.
 * @param {Array} media - Array of { value, is_primary, type, ... }
 * @returns {string|undefined}
 */
export function getPrimaryMediaUrl(media) {
  return media?.find((m) => m.is_primary)?.value ?? media?.[0]?.value;
}

/**
 * Normalises a user-entered string before it reaches the API:
 *   - Curly/smart quotes → straight quotes
 *   - Curly apostrophes → straight apostrophe
 *   - Em/en dashes → hyphen
 *   - Ellipsis character → three dots
 *   - Zero-width and non-breaking spaces → regular space
 *   - Trims leading/trailing whitespace
 *
 * Returns undefined for falsy input so callers can safely use `?? ""` or
 * conditional spreading.
 *
 * @param {string|undefined|null} str
 * @returns {string|undefined}
 */
export function sanitizeText(str) {
  if (!str) return undefined;
  return (
    str
      .replace(/[\u201C\u201D\u201E\u201F\u275D\u275E]/g, '"') // curly double quotes → "
      .replace(/[\u2018\u2019\u201A\u201B\u275B\u275C]/g, "'") // curly single quotes → '
      .replace(/[\u2013\u2014\u2015]/g, "-") // en/em dash → hyphen
      .replace(/\u2026/g, "...") // ellipsis → ...
      .replace(/[\u00A0\uFEFF]/g, " ") // non-breaking space, BOM
      .replace(/[\u200B\u200C\u200D]/g, " ") // zero-width chars
      .replace(/[\v\f]/g, " ") // vertical tab, form feed → space
      .replace(/\\n|\/n/gi, " ") // literal \n or /n typed by users
      // eslint-disable-next-line no-control-regex
      .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, "") // harmful control chars (keeps \t \n \r)
      .replace(/[^\S\n\r]{2,}/g, " ") // collapse multiple spaces (preserves newlines)
      .replace(/\n{3,}/g, "\n\n") // collapse 3+ consecutive newlines to max 2
      .trim()
  );
}

/**
 * Ant Design form rule validator for any user-entered text field.
 * Rejects values that contain control characters, literal /n or \n,
 * or consist entirely of punctuation / symbols.
 *
 * Usage:  rules={[{ validator: textInputValidator }]}
 */
export const textInputValidator = (_, value) => {
  if (!value) return Promise.resolve();
  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/.test(value))
    return Promise.reject(
      new Error("Contains invisible control characters. Please retype it.")
    );
  if (/[/\\]n/i.test(value))
    return Promise.reject(new Error('Must not contain "/n" or "\\n".'));
  const hasAlphanumeric =
    /[a-zA-Z0-9]/.test(value) ||
    /[\u0900-\u097F]/.test(value) ||
    /[\u0980-\u09FF]/.test(value) ||
    /[\u0C00-\u0C7F]/.test(value);
  if (!hasAlphanumeric)
    return Promise.reject(
      new Error("Must contain at least one letter or number.")
    );
  return Promise.resolve();
};

export const getColSpan = (isEdit = false, noOfColumns = 1) => {
  if (isEdit) return { xs: 24, sm: 24 , md: 24/noOfColumns };
  return { xs: 24, sm: 24 , md: 24/noOfColumns };
};

export const phoneNumberValidator = (value) => {
  if (Array.isArray(value)) {
    return value.filter((p) => !/^\d{10}$/.test(p.trim().replace(/\D/g, "")));
  }

  if (typeof value === "string") {
    return [value].filter((p) => !/^\d{10}$/.test(p.trim().replace(/\D/g, "")));
  }

  return [];
};

export const emailValidator = (value) => {
  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (Array.isArray(value)) {
    return value.filter((e) => !emailRe.test(e.trim()));
  }

  if (typeof value === "string") {
    return [value].filter((e) => !emailRe.test(e.trim()));
  }

  return [];
};

export const websiteValidator = (value) => {
  const urlRe = /^https?:\/\/.+\..+/;

  if (Array.isArray(value)) {
    return value.filter((w) => !urlRe.test(w.trim()));
  }

  if (typeof value === "string") {
    return [value].filter((w) => !urlRe.test(w.trim()));
  }

  return [];
};
